import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { of, Subscription, switchMap } from 'rxjs';
import { CredentialTransitionStatus } from '../../../../models/credentialTransitionStatus';
import { CredentialTransitionService } from '../../../../core/services/credential-transition.service';
import { OnboardingService } from '../../../../core/services/onboarding.service';
import { LicenseInfosModel } from '../../../../models/onBoardingModels';

@Component({
  selector: 'app-credential-transition',
  templateUrl: './credential-transition.component.html',
  standalone: false
})
export class CredentialTransitionComponent implements OnChanges, OnDestroy
{
  @Input() status: CredentialTransitionStatus;
  @Input() userId: number;
  selectedVersionID: number;
  examDate = '';
  busy = false;
  error = '';
  showUpload = false;
  confirmedRbt = false;
  file: File;
  license: LicenseInfosModel =
  {
    licenseNumber: '',
    isBoardCertification: null,
    isTransitionCredential: true
  };
  private subscriptions = new Subscription();

  constructor(private service: CredentialTransitionService, private onboarding: OnboardingService)
  {
  }

  ngOnChanges()
  {
    if (!this.busy)
      this.examDate = this.status?.examDate?.substring(0, 10) || '';
  }

  begin()
  {
    if (this.busy || !this.selectedVersionID)
      return;

    this.busy = true;
    this.error = '';

    this.subscriptions.add(
      this.service.begin(this.selectedVersionID).subscribe(
        {
          next: status => this.acceptStatus(status),
          error: e => this.showError(e)
        })
    );
  }

  get selectedOption()
  {
    return this.status?.options?.find(o => o.processVersionID === this.selectedVersionID);
  }

  saveDate()
  {
    if (this.busy || !this.status?.canEdit || !this.examDate)
      return;

    const submittedDate = this.examDate;
    this.busy = true;
    this.error = '';

    this.subscriptions.add(
      this.service.saveExamDate(submittedDate).subscribe(
        {
          next: status => this.acceptStatus(status),
          error: error =>
          {
            this.examDate = this.status?.examDate?.substring(0, 10) || '';
            this.showError(error);
          }
        })
    );
  }

  confirmPayDecrease()
  {
    if (this.busy
      || !this.status?.payDecreaseWarning
      || !window.confirm('This offer will decrease your current pay. Are you sure you want to generate and send it?'))
      return;

    this.busy = true;
    this.error = '';

    this.subscriptions.add(
      this.service.confirmPayDecrease(this.status.attemptID, this.status.processVersionID)
        .pipe(switchMap(() => this.service.getStatus()))
        .subscribe(
          {
            next: status => this.acceptStatus(status),
            error: e => this.showError(e)
          })
    );
  }

  refresh()
  {
    if (this.busy)
      return;

    this.busy = true;
    this.error = '';

    this.subscriptions.add(
      this.service.getStatus().subscribe(
        {
          next: status => this.acceptStatus(status),
          error: error => this.showError(error)
        })
    );
  }

  chooseFile(event: Event)
  {
    this.file = (event.target as HTMLInputElement).files?.[0];
    this.license.transitionUploadToken = null;
  }

  upload()
  {
    if (this.busy
      || !this.status?.canUploadCredential
      || !this.confirmedRbt
      || this.license.isBoardCertification == null
      || !this.file
      || !this.license.originationDate
      || !this.license.issueDate
      || !this.license.licenseNumber)
      return;

    if (!this.status.credentialUploadTypeID)
    {
      this.error = 'The Licenses/Certifications upload type is not configured. Contact HR.';
      return;
    }

    const file = this.file;
    const license = { ...this.license };
    const employeeId = this.userId;
    this.busy = true;
    this.error = '';

    const operation = license.transitionUploadToken
      ? of({ uploadToken: license.transitionUploadToken })
      : this.service.beginCredentialUpload();

    this.subscriptions.add(
      operation.pipe(
        switchMap(result =>
        {
          this.license.transitionUploadToken = result.uploadToken;
          license.transitionUploadToken = result.uploadToken;

          return this.onboarding.uploadFile(
            this.status.credentialUploadTypeID.toString(),
            [file],
            employeeId,
            true,
            undefined,
            license
          );
        }),
        switchMap(() => this.service.getStatus())
      ).subscribe(
        {
          next: status =>
          {
            this.showUpload = false;
            this.file = null;
            this.confirmedRbt = false;
            this.acceptStatus(status);
          },
          error: error => this.showError(error)
        })
    );
  }

  acknowledge()
  {
    if (this.busy)
      return;

    this.busy = true;
    this.error = '';

    this.subscriptions.add(
      this.service.acknowledgeSignaturePrompt().subscribe(
        {
          next: status => this.acceptStatus(status),
          error: error => this.showError(error)
        })
    );
  }

  private acceptStatus(status: CredentialTransitionStatus)
  {
    this.status = status;
    this.examDate = status.examDate?.substring(0, 10) || '';
    this.busy = false;
  }

  private showError(error: any)
  {
    this.error = typeof error?.error === 'string'
      ? error.error
      : error?.error?.message || error?.message || 'The request failed. Please try again.';
    this.busy = false;
  }

  ngOnDestroy()
  {
    this.subscriptions.unsubscribe();
  }
}
